import { useGA4Tracking } from '@/composables/useGA4Tracking'
import {
	FOOTER_NAV_LOCATION_ID,
	MOBILE_FOOTER_NAV_LOCATION_ID
} from '@/constants/ga4/tracking-ids/layout/footer'
import {
	HEADER_NAV_LOCATION_ID,
	MOBILE_DRAWER_NAV_LOCATION_ID,
	MOBILE_DRAWER_NAV_TOGGLE_BUTTON_ID
} from '@/constants/ga4/tracking-ids/layout/header'
import {
	HELP_CENTER_BASE_URL_EN,
	HELP_CENTER_BASE_URL_ES
} from '@/constants/index.js'
import type { NavigationMenuItem } from '@nuxt/ui'
import { useWindowSize } from '@vueuse/core'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { width } = useWindowSize()

const isMobile = computed(() => width.value < 768)

type NavLocation = typeof HEADER_NAV_LOCATION_ID | typeof FOOTER_NAV_LOCATION_ID | typeof MOBILE_DRAWER_NAV_LOCATION_ID | typeof MOBILE_FOOTER_NAV_LOCATION_ID

/**
 * Button text patterns to exclude from navigation tracking.
 * These are handled separately via explicit button event handlers in the template.
 * Add new patterns here as needed.
 */
const HEADER_EXCLUDED_BUTTON_TEXTS = {
	en: [
		'check availability',
		'sign in',
		'your account'
	],
	es: [
		'verificar disponibilidad',
		'iniciar sesión',
		'tu cuenta'
	]
} as const

const FOOTER_EXCLUDED_BUTTON_ARIA_LABELS = [
	'Facebook',
	'Nextdoor',
	'Google'
] as const

const FOOTER_EXCLUDED_BUTTON_TEXTS = {
	en: [
		'pay my bill'
	],
	es: [
		'pagar mi factura'
	]
} as const

interface LocalizationConfig {
	slot: string
	label: string
	es: {
		id: string
		label: string
		key: string
		route: string
	}
	en: {
		id: string
		label: string
		key: string
		route: string
	}
}

/**
 * Localization configuration for language toggle tracking.
 * Add new languages here as needed.
 */
const LOCALIZATION: LocalizationConfig = {
	slot: 'localization',
	label: 'Localization',
	es: {
		id: 'es',
		label: 'Español',
		key: 'español',
		route: '/es'
	},
	en: {
		id: 'en',
		label: 'English',
		key: 'english',
		route: '/en'
	}
} as const

interface ExternalLinkConfig {
	parent: { // NOTE: DON'T include ES localization here, as the lables are just used for tracking meta.
		label: string
		key: string
	}
	child: {
		label: string
		key: string
	}
	urls: string[] // NOTE: DO include both EN and ES URLs here if applicable
}

// Help Center external link configuration.
const HELP_CENTER: ExternalLinkConfig = {
	parent: {
		label: 'Support',
		key: 'support'
	},
	child: {
		label: 'Help Center',
		key: 'help center'
	},
	urls: [
		`${HELP_CENTER_BASE_URL_EN}`,
		`${HELP_CENTER_BASE_URL_ES}` // NOTE: (7-7-25) Not used in the app yet, but included for future localization
	] as string[]
} as const

/**
 * SPECIAL CASE: External Links
 *
 * IMPORTANT: This handles a special case where NuxtUI's external link handling
 * was causing href mismatches. We track these URLs directly by exact match.
 */
const EXTERNAL_LINKS: ExternalLinkConfig[] = [
	HELP_CENTER
	// ... Add more external link configs here as needed
] as const

/**
 * ## Navigation Tracking Composable
 *
 * ### Handles all GA4 event tracking for navigation interactions in the AppHeader component.
 *
 * This includes hover and click tracking for **parent nav items**, **child dropdown items**,
 * and **special handling** for:
 * - slotted nav items (e.g. localization)
 * - external links (e.g. Help Center).
 *
 * @example
 * ```vue
 * // Component Usage:
 * <script setup>
 * const { setupHeaderNavigationTracking } = useNavigationTracking()
 *
 * onMounted(() => {
 *   nextTick(() => {
 *     setupHeaderNavigationTracking(items)
 *   })
 * })
 * </script>
 * ```
 */
export const useNavigationTracking = () => {
	const {
		track, trackNavigationHover, trackNavigationClick
	} = useGA4Tracking()

	const { locale } = useI18n({ useScope: 'global' })

	// Prevents duplicate hover events when mouse moves slightly within the same nav item
	const lastHoveredChild = ref<string | null>(null)

	/**
	 * Track hover events for parent navigation items (e.g., "Services", "About") - desktop only
	 */
	const trackParentHover = (parentLabel: string, location: NavLocation, parameters?: Record<string, any>) => {
		trackNavigationHover(parentLabel, location, parameters)
	}

	/**
	 * Generate unique identifier for child hover tracking to prevent duplicates
	 */
	const createChildHoverId = (location: string, parentLabel: string, childLabel: string): string =>
		`${location}_${parentLabel.toLowerCase()}_${childLabel.toLowerCase()}`

	/**
	 * Generate unique identifier for parent hover tracking to prevent duplicates
	 */
	const createParentHoverId = (location: string, itemLabel: string): string =>
		`${location}_parent_${itemLabel.toLowerCase()}`

	/**
	 * Track hover events for child navigation items (e.g., "Services > Residential")
	 */
	const trackChildHover = (parentLabel: string, childLabel: string, location: NavLocation, parameters?: Record<string, any>) => {
		trackNavigationHover(`${parentLabel} > ${childLabel}`, location, parameters)
	}

	/**
	 * Track click events for child navigation items (e.g., "Services > Residential")
	 */
	const trackChildClick = (parentLabel: string, childLabel: string, location: NavLocation, parameters?: Record<string, any>) => {
		trackNavigationClick(`${parentLabel} > ${childLabel}`, getDeviceSpecificLocation(location), parameters)
	}

	const getDeviceSpecificLocation = (location: NavLocation): NavLocation => {
		switch (location) {
			case HEADER_NAV_LOCATION_ID:
				return isMobile.value ? MOBILE_DRAWER_NAV_LOCATION_ID : HEADER_NAV_LOCATION_ID
			case FOOTER_NAV_LOCATION_ID:
				return isMobile.value ? MOBILE_FOOTER_NAV_LOCATION_ID : FOOTER_NAV_LOCATION_ID
			case MOBILE_DRAWER_NAV_LOCATION_ID:
				return MOBILE_DRAWER_NAV_LOCATION_ID // Mobile header nav is always mobile
			case MOBILE_FOOTER_NAV_LOCATION_ID:
				return MOBILE_FOOTER_NAV_LOCATION_ID // Mobile footer nav is always mobile
			default:
				return HEADER_NAV_LOCATION_ID // Default to header nav if unknown
		}
	}

	/**
	 * Find matching navigation item configuration for a DOM element.
	 *
	 * @param items - Array of navigation menu items to search
	 * @param element - DOM element that was clicked/hovered
	 * @param href - Pre-extracted href value of the link (if available)
	 * @returns Object containing parent and child navigation items, or empty object
	 */
	const findNavigationItem = (items: NavigationMenuItem[], element: HTMLElement, href?: string): {
		parent?: NavigationMenuItem
		child?: NavigationMenuItem
	} => {
		const actualHref = href || element.getAttribute('href')

		if (!actualHref) return {}

		for (const item of items) {
			if (!item.children) continue

			for (const child of item.children) {
				if (actualHref && child.to && actualHref === child.to) {
					return {
						parent: item,
						child
					}
				}
			}
		}

		return {}
	}

	/**
	 * Mobile navigation state tracking
	 * This tracks which parent items are currently active (expanded) in the mobile navigation accordion
	 */
	const activeMobileParents = ref<string[]>([])

	/**
	 * Resets the active states for mobile navigation parents.
	 * Call this when the mobile navigation drawer is closed to clear state.
	 */
	const resetMobileActiveStates = () => {
		activeMobileParents.value = []
	}

	const trackNavigationDrawerOpen = () => {
		track('button_click', {
			button_id: MOBILE_DRAWER_NAV_TOGGLE_BUTTON_ID,
			button_type: 'button',
			action: 'open'
		})
	}

	const handleMobileHeaderNavigationOpened = (opened: boolean) => {
		if (!opened) {
			// Reset state when closed to avoid stale data
			resetMobileActiveStates()
			track('button_click', {
				button_id: MOBILE_DRAWER_NAV_TOGGLE_BUTTON_ID,
				button_type: 'button',
				action: 'close'
			})
			return
		} else { // If opened, track the event
			trackNavigationDrawerOpen()
		}
	}

	/**
	 * Handles mobile navigation accordion tracking.
	 * Detects when parent items are opened or closed and automatically tracks the events.
	 *
	 * @param items - Array of navigation menu items to search
	 * @param currentIds - Current array of active item IDs (e.g. ["item-0", "item-2"])
	 */
	const handleMobileHeaderNavigationTracking = (items: NavigationMenuItem[], currentIds: string[] = []) => {
		const previousIds = activeMobileParents.value

		// Find newly opened items (in current but not in previous)
		const openedIds = currentIds.filter(id => !previousIds.includes(id))

		// Find newly closed items (in previous but not in current)
		const closedIds = previousIds.filter(id => !currentIds.includes(id))

		// Update the stored state
		activeMobileParents.value = [ ...currentIds ]

		// Handle opened items
		if (openedIds.length > 0) {
			const itemId = openedIds[0] // Handle first opened item
			if (!itemId) return

			const itemIndex = parseInt(itemId.replace('item-', ''), 10)

			if (!isNaN(itemIndex) && itemIndex >= 0 && itemIndex < items.length) {
				const item = items[itemIndex]

				if (item?.label) {
					trackNavigationClick(
						item.label,
						MOBILE_DRAWER_NAV_LOCATION_ID,
						{
							route: item.to || '',
							state: 'opened'
						}
					)
				}
			}
		}

		// Handle closed items
		if (closedIds.length > 0) {
			const itemId = closedIds[0] // Handle first closed item
			if (!itemId) return

			const itemIndex = parseInt(itemId.replace('item-', ''), 10)

			if (!isNaN(itemIndex) && itemIndex >= 0 && itemIndex < items.length) {
				const item = items[itemIndex]

				if (item?.label) {
					// Track the mobile navigation accordion closed event
					trackNavigationClick(
						item.label,
						MOBILE_DRAWER_NAV_LOCATION_ID,
						{
							route: item.to || '',
							state: 'closed'
						}
					)
				}
			}
		}
	}

	/**
	 * Check if link text matches excluded button patterns.
	 * Excluded buttons have explicit tracking in the template.
	 */
	const isExcludedButton = (linkText: string, location: NavLocation): boolean => {
		if (location.includes(HEADER_NAV_LOCATION_ID)) {
			return [ ...HEADER_EXCLUDED_BUTTON_TEXTS.en, ...HEADER_EXCLUDED_BUTTON_TEXTS.es ]
				.some(text => linkText.toLowerCase().includes(text))
		} else if (location.includes(FOOTER_NAV_LOCATION_ID)) {
			// Footer excluded buttons can be added here if needed
			return [ ...FOOTER_EXCLUDED_BUTTON_ARIA_LABELS, ...FOOTER_EXCLUDED_BUTTON_TEXTS.en, ...FOOTER_EXCLUDED_BUTTON_TEXTS.es ]
				.some(text => linkText.toLowerCase().includes(text))
		}
		return false // Default to no exclusions
	}

	/**
	 * SPECIAL CASE: External Links
	 *
	 * Handle external link tracking for special cases like Help Center.
	 * This function checks if the href matches any configured external link
	 * and tracks it accordingly.
	 * @param href - The href attribute of the link
	 * @param isClick - Whether this is a click event (true) or hover (false)
	 * @returns true if the link was handled as an external link, false otherwise
	 */
	const handleExternalLinkTracking = (href: string, location: NavLocation, isClick: boolean) => {
		// Check if the href matches any configured external link
		for (const linkConfig of EXTERNAL_LINKS) {
			if (linkConfig.urls.includes(href)) {
				if (isClick) {
					trackChildClick(linkConfig.parent.label, linkConfig.child.label, location, { route: href })
				} else {
					const hoverId = createChildHoverId(location, linkConfig.parent.key, linkConfig.child.key)
					if (lastHoveredChild.value !== hoverId) {
						lastHoveredChild.value = hoverId
						trackChildHover(linkConfig.parent.label, linkConfig.child.label, location, { route: href })
					}
				}
				return true // External link handled
			}
		}
		return false // Not an external link
	}

	/**
	 * Setup function to initialize footer navigation tracking.
	 *
	 * Call this in onMounted() with nextTick() to ensure DOM is ready.
	 * Attaches event listeners to the footer element for comprehensive tracking.
	 *
	 * @param items - Footer navigation menu items configuration array
	 */
	const setupFooterNavigationTracking = (items: NavigationMenuItem[]) => {
		const footerElement = document.querySelector('footer')
		if (!footerElement) return

		/**
		 * Core event handler for footer navigation interactions.
		 * Handles the complex logic of determining what was clicked/hovered
		 * and dispatches appropriate tracking events.
		 */
		const handleFooterNavigationEvent = (event: Event, isClick = false) => {
			const target = event.target as HTMLElement
			const link = target.closest('a[href]') || target.closest('button[type="button"]')

			if (!link) return

			const linkText = link.textContent?.trim() || ''
			const href = link.getAttribute('href') || ''

			// Skip excluded buttons and empty links
			if (!linkText || isExcludedButton(linkText, FOOTER_NAV_LOCATION_ID)) return

			// Handle external links (like Help Center)
			if (EXTERNAL_LINKS.some(linkConfig => linkConfig.urls.includes(href))) {
				if (handleExternalLinkTracking(href, FOOTER_NAV_LOCATION_ID, isClick)) return
			}

			// Find matching navigation item for footer links
			const {
				parent, child
			} = findNavigationItem(items, link as HTMLElement, href)

			if (parent?.label && child?.label) {
				if (isClick) {
					trackChildClick(parent.label, child.label, FOOTER_NAV_LOCATION_ID, { route: href })
				} else {
					const hoverId = createChildHoverId(FOOTER_NAV_LOCATION_ID, parent.label, child.label)
					if (lastHoveredChild.value !== hoverId) {
						lastHoveredChild.value = hoverId
						trackChildHover(parent.label, child.label, FOOTER_NAV_LOCATION_ID, { route: href })
					}
				}
				return
			}

			// Handle direct footer links (like Terms of Service, Privacy Policy)
			if (href && linkText) {
				if (isClick) {
					trackNavigationClick(linkText, FOOTER_NAV_LOCATION_ID, { route: href })
				} else {
					const hoverId = createParentHoverId(FOOTER_NAV_LOCATION_ID, linkText)
					if (lastHoveredChild.value !== hoverId) {
						lastHoveredChild.value = hoverId
						trackNavigationHover(linkText, FOOTER_NAV_LOCATION_ID, { route: href })
					}
				}
			}
		}

		// Attach event listeners to footer element
		footerElement.addEventListener('mouseover', event => handleFooterNavigationEvent(event, false))
		footerElement.addEventListener('click', event => handleFooterNavigationEvent(event, true))
		footerElement.addEventListener('touchstart', event => handleFooterNavigationEvent(event, true))

		// Reset hover state when mouse leaves footer area
		footerElement.addEventListener('mouseleave', () => {
			lastHoveredChild.value = null
		})
	}

	/**
	 * Main setup function to initialize navigation tracking.
	 *
	 * Call this in onMounted() with nextTick() to ensure DOM is ready.
	 * Attaches event listeners to both header and mobile drawer for comprehensive tracking.
	 *
	 * @param items - Desktop navigation menu items configuration array
	 */
	const setupHeaderNavigationTracking = (items: NavigationMenuItem[]) => {
		const headerElement = document.querySelector('header')
		if (!headerElement) return

		/**
		 * Core event handler for all navigation interactions.
		 * Handles the complex logic of determining what was clicked/hovered
		 * and dispatches appropriate tracking events.
		 */
		const handleHeaderNavigationEvent = (event: Event, isClick = false) => {
			// Handle clicks on links or buttons within the navigation
			const target = event.target as HTMLElement
			const link = target.closest('a[href]') || target.closest('button[type="button"]')
			if (!link) return

			const linkText = link.textContent?.trim() || ''
			const href = link.getAttribute('href') || ''

			// SPECIAL CASE: Handle External links
			// These URLs need direct matching due to NuxtUI external link handling quirks
			if (EXTERNAL_LINKS.some(linkConfig => linkConfig.urls.includes(href))) {
				if (handleExternalLinkTracking(href, HEADER_NAV_LOCATION_ID, isClick)) return
			}

			// Skip excluded buttons (handled explicitly in template)
			if (!linkText || isExcludedButton(linkText, HEADER_NAV_LOCATION_ID)) return

			// Child navigation items (dropdown links)
			const {
				parent, child
			} = findNavigationItem(items, link as HTMLElement, href)

			if (parent?.label && child?.label) {
				if (isClick) {
					trackChildClick(parent.label, child.label, HEADER_NAV_LOCATION_ID, { link_to: href })
				} else {
					const hoverId = createChildHoverId(HEADER_NAV_LOCATION_ID, parent.label, child.label)
					if (lastHoveredChild.value !== hoverId) {
						lastHoveredChild.value = hoverId
						trackChildHover(parent.label, child.label, HEADER_NAV_LOCATION_ID, { link_to: href })
					}
				}
				return
			}

			// Standalone parent items (Construction, Localization)
			// These don't have children and are tracked as parent hovers only
			if (!isClick) {
				const parentItem = items.find(item =>
					(item.label && linkText.includes(item.label))
					|| (item.slot && (linkText.toLowerCase().includes(LOCALIZATION.en.key) || linkText.toLowerCase().includes(LOCALIZATION.es.key)))
				)

				if (parentItem) {
					const isLocalization = parentItem.slot === LOCALIZATION.slot
					const itemLabel = isLocalization
						? (locale.value === LOCALIZATION.es.id ? LOCALIZATION.en.label : LOCALIZATION.es.label) // Show opposite language
						: parentItem.label
					const itemRoute = isLocalization
						? (locale.value === LOCALIZATION.es.id ? LOCALIZATION.en.route : LOCALIZATION.es.route) // Link to opposite language
						: (href || parentItem.to || '')

					if (itemLabel) {
						const hoverId = createParentHoverId(HEADER_NAV_LOCATION_ID, itemLabel)
						if (lastHoveredChild.value !== hoverId) {
							lastHoveredChild.value = hoverId
							trackParentHover(itemLabel, HEADER_NAV_LOCATION_ID, { link_to: itemRoute })
						}
					}
				}
			}
		}

		// Attach event listeners to header element
		headerElement.addEventListener('mouseover', event => handleHeaderNavigationEvent(event, false))
		headerElement.addEventListener('click', event => handleHeaderNavigationEvent(event, true))
		headerElement.addEventListener('touchstart', event => handleHeaderNavigationEvent(event, true))

		// Reset hover state when mouse leaves navigation area (prevents stuck hover states)
		headerElement.addEventListener('mouseleave', () => {
			lastHoveredChild.value = null
		})
	}

	return {
		setupHeaderNavigationTracking,
		setupFooterNavigationTracking,
		trackParentHover,
		handleMobileHeaderNavigationTracking,
		handleMobileHeaderNavigationOpened
	}
}

/**
 * MAINTENANCE NOTES FOR DEVELOPERS:
 *
 * 🔧 Adding New Navigation Items:
 * - Items with children: Work automatically via findNavigationItem()
 * - Standalone items: Add logic in "SPECIAL CASE: Standalone parent items" section
 * - External links: Add to EXTERNAL_LINKS config
 *
 * 🐛 Common Issues:
 * - Duplicate events: Check hover ID generation logic (solved by lastHoveredChild)
 * - Missing events: Verify DOM structure matches isMobileNavigation() selectors
 * - Wrong parent/child mapping: Debug findNavigationItem() with navigation config
 *
 * 🧪 Testing:
 * - Test both desktop and mobile navigation (ctr + shift + G to toggle GA4 debugger tool)
 * - Verify hover deduplication works (prevents duplicates on mouse micro-movements)
 * - Check external links track correctly
 * - Ensure excluded buttons don't create duplicate events
 *
 * 📊 GA4 Event Structure:
 * - Parent hovers: "About" (location: HEADER_NAV_LOCATION_ID)
 * - Child interactions: "Services > Residential" (location: HEADER_NAV_LOCATION_ID/mobile_nav)
 * - All events include route parameter for destination tracking
 */
